import { Field } from '@base-ui/react/field';
import type { KeyboardEvent } from 'react';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { useI18n } from '../../i18n/i18n';
import { translateValidation } from '../../i18n/validation';

/** Подпись поля — как `.diagrams-form-label` прототипа: 11 px, заглавные, разрядка, серый. */
export const FIELD_LABEL = 'text-2xs font-semibold tracking-[0.06em] text-hint uppercase';

interface TextFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  type?: 'text' | 'email' | 'password';
  autoComplete?: string;
  inputMode?: 'text' | 'numeric' | 'email';
  description?: string;
  autoFocus?: boolean;
  /** Многострочное поле (сообщение): textarea вместо input. */
  multiline?: boolean;
  placeholder?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * Поле формы: Base UI Field + react-hook-form (AGENTS.md §17). Текст ошибки валидации —
 * ключ словаря из zod-схемы формы, переводится здесь.
 */
export function TextField<T extends FieldValues>({
  control,
  name,
  label,
  type = 'text',
  autoComplete,
  inputMode,
  description,
  autoFocus,
  multiline = false,
  placeholder,
  onKeyDown,
}: TextFieldProps<T>) {
  const { t } = useI18n();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field.Root
          name={field.name}
          invalid={fieldState.invalid}
          touched={fieldState.isTouched}
          dirty={fieldState.isDirty}
          className="flex flex-col gap-1.5"
        >
          <Field.Label className={FIELD_LABEL}>{label}</Field.Label>
          <Field.Control
            ref={field.ref}
            type={type}
            value={field.value ?? ''}
            onBlur={field.onBlur}
            onValueChange={field.onChange}
            autoComplete={autoComplete}
            inputMode={inputMode}
            autoFocus={autoFocus}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            {...(multiline ? { render: <textarea rows={4} /> } : {})}
            className={`${multiline ? 'min-h-[60px] resize-y px-2.5 py-2 text-[13px]' : 'h-10 px-3 text-sm'} rounded-sm border-[1.25px] border-border bg-input outline-none hover:border-border-hover focus:border-primary focus:bg-card focus:shadow-[var(--shadow-focus-ring)] data-[invalid]:border-danger`}
          />
          {description !== undefined && (
            <Field.Description className="text-2xs text-muted-foreground">
              {description}
            </Field.Description>
          )}
          <Field.Error match={fieldState.invalid} className="text-2xs text-danger">
            {translateValidation(t, fieldState.error?.message)}
          </Field.Error>
        </Field.Root>
      )}
    />
  );
}
